import { Link } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import PageHeader from "@/components/feature/PageHeader";
import SectionHeading from "@/components/feature/SectionHeading";
import { brand, storyPoints } from "@/mocks/siteData";

const phaseIcon: Record<string, string> = {
  序章: "ri-door-open-line",
  第一章: "ri-eye-line",
  第二章: "ri-heart-line",
  第三章: "ri-compass-3-line",
};

const phaseColor: Record<string, string> = {
  序章: "bg-primary-500",
  第一章: "bg-accent-500",
  第二章: "bg-secondary-500",
  第三章: "bg-foreground-800",
};

export default function Story() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "/" },
          { "@type": "ListItem", position: 2, name: "剧情与世界观", item: "/story" },
        ],
      },
      {
        "@type": "Article",
        headline: "洞洞杂货铺剧情与世界观解析",
        description:
          "从继承杂货店、墙上的洞，到村里的人情与横跨百年的真相，梳理《洞洞杂货店》的剧情脉络与世界观。",
        inLanguage: "zh-CN",
      },
    ],
  };

  return (
    <>
      <Seo
        title="洞洞杂货铺剧情与世界观｜剧情脉络与世界观解析"
        description="洞洞杂货铺剧情与世界观解析：从继承杂货店、墙上的洞，到村里的人情与横跨百年的真相，梳理整部作品的剧情脉络与设定。"
        keywords="洞洞杂货铺剧情,洞洞杂货铺世界观,剧情解析,悬疑漫画"
        path="/story"
        ogType="article"
        jsonLd={jsonLd}
      />
      <PageHeader
        crumb={[{ label: "首页", to: "/" }, { label: "剧情与世界观" }]}
        title="剧情与世界观"
        subtitle="继承杂货店、墙上的洞、村里的人情，再到横跨百年的真相——四段脉络，勾勒出《洞洞杂货店》的世界观轮廓。"
      />

      <section className="w-full bg-background-50 py-12 md:py-16">
        <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
          <ol className="relative space-y-10 border-l-2 border-background-300/70 pl-8 md:pl-12">
            {storyPoints.map((p, idx) => (
              <li key={p.id} className="relative">
                <span
                  className={`absolute -left-[41px] flex h-5 w-5 items-center justify-center rounded-full ring-4 ring-background-50 md:-left-[57px] ${phaseColor[p.phase]}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-background-50" />
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-md bg-background-200/70 px-2.5 py-1 text-xs font-bold text-foreground-700">
                    <i className={`${phaseIcon[p.phase] ?? "ri-star-line"} text-primary-700`} />
                    {p.phase}
                  </span>
                  <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-bold text-accent-800">
                    {p.tag}
                  </span>
                  <span className="text-xs text-foreground-400">Part {String(idx + 1).padStart(2, "0")}</span>
                </div>
                <h2 className="mt-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {p.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-foreground-700 md:text-base">
                  {p.text}
                </p>
              </li>
            ))}
          </ol>

          <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100 p-6">
            <SectionHeading
              align="left"
              eyebrow="阅读建议"
              title={`正篇 ${brand.episodes}`}
              desc="单元故事与主线相互穿插，既可碎片追更，也适合连贯阅读。看完这份资料后，直接前往正版平台感受完整的悬疑氛围与人情温度吧。"
            />
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/characters"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                回看角色介绍
              </Link>
              <Link
                to="/settings"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-accent-300 bg-background-50 px-5 py-2.5 text-sm font-bold text-accent-700 transition-colors hover:bg-accent-50"
              >
                查看店铺设定
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}